
import { computed, defineComponent, ref } from "vue";
import Menuitem from "@/models/MenuItem";
import Menu from "primevue/menu";
import useOidc from "@/composables/useOidc";
import MenuItem from "@/models/MenuItem";
import useUser from "@/composables/useUser";
import { useRouter, useRoute } from "vue-router";
import { isValidGuid } from "@/composables/utils";

export default defineComponent({
  setup() {
    const router = useRouter();
    const route = useRoute();

    const menu = ref<Menu | null>(null);
    const menuUser = ref<Menu | null>(null);
    const settingsMenu = ref<Menu | null>(null);
    const oidc = useOidc();
    const user = useUser();
    const menuItemsComputed = useMenuitems((path) => router.push(path));
    const isSettingsActive = computed(() => route.path.startsWith("/setting"));
    const settingsSubItems = computed(
      () => menuItemsComputed.value.find((item) => item.items)?.items ?? []
    );

    return {
      oidcUser: oidc.user,
      user: user.user,
      menu,
      menuUser,
      settingsMenu,
      isSettingsActive,
      settingsSubItems,
      menuItems: menuItemsComputed,
      userMenuItems: useUserMenuItems(oidc.logOut, (path) => router.push(path)),
      toggle: (event: Event) => {
        menu.value?.toggle(event);
        menuUser.value?.toggle(event);
      },
      toggleSettingsMenu: (event: Event) => {
        settingsMenu.value?.toggle(event);
      },
    };
  },
});

function useMenuitems(navigate: (path: string) => void) {
  const user = useUser().user;
  return computed<MenuItem[]>(() =>
    user.value?.isSuperAdmin
      ? [
          {
            label: "Organisationer",
            to: "/organizations",
          },
          {
            label: "Applikationer",
            to: "/applications",
          },
          {
            label: "Profilsøgning",
            to: "/users",
          },
          {
            label: "Logs",
            to: "/logs",
          },
          {
            label: "Indstillinger",
            items: [
              { label: "Indstillinger", command: () => navigate("/setting") },
              {
                label: "Batch rolletildeling",
                command: () => navigate("/setting/batch-role-assignment"),
              },
              {
                label: "Forbindelsesoversigt",
                command: () => navigate("/setting/forbindelsesoversigt"),
              },
            ],
          },
        ]
      : user.value?.isUserAdmin
      ? [
          {
            label: "Organisationer",
            to: "/organizations",
          },
          {
            label: "Applikationer",
            to: "/applications",
          },
          {
            label: "Profilsøgning",
            to: "/users",
          },
          {
            label: "Logs",
            to: "/logs",
          },
        ]
      : user.value?.isApplicationAdmin
      ? [
          {
            label: "Organisationer",
            to: "/organizations",
          },
          {
            label: "Applikationer",
            to: "/applications",
          },
          {
            label: "Profilsøgning",
            to: "/users",
          },
          {
            label: "Logs",
            to: "/logs",
          },
        ]
      : user.value?.isAdmin
      ? [
          {
            label: "Logs",
            to: "/logs",
          },
        ]
      : []
  );
}

function useUserMenuItems(
  logout: () => void,
  navigate: (path: string) => void
) {
  const user = useUser().user;

  return computed<Menuitem[]>(() => {
    const menuItems: Menuitem[] = [];

    // Only add "Min profil" if user has a valid GUID
    if (isValidGuid(user.value?.userId)) {
      menuItems.push({
        label: "Min profil",
        command: () => navigate("/me"),
      });
    }

    const isAdmin =
      user.value?.isAdmin ||
      user.value?.isSuperAdmin ||
      user.value?.isUserAdmin ||
      user.value?.isApplicationAdmin;

    // Always add "Claims oversigt"
    menuItems.push({
      label: "Claims oversigt",
      command: () => navigate("/claims-overview"),
    });

    // Always add logout
    menuItems.push({
      label: "Log ud",
      command: () => logout(),
    });

    return menuItems;
  });
}
